import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import {
  disableTeamEnrichment,
  getTeamEnrichmentStatus,
  setTeamEnrichmentApiKey,
  type EnrichmentProvider,
  type TeamEnrichmentStatus,
} from '../lib/teamEnrichment'

export const teamEnrichmentKey = (
  teamId: string | null | undefined,
  provider: EnrichmentProvider = 'harmonic',
): QueryKey => ['teamEnrichment', teamId ?? 'none', provider]

export function useTeamEnrichmentProvider(
  teamId: string | null | undefined,
  provider: EnrichmentProvider = 'harmonic',
) {
  const qc = useQueryClient()
  const key = teamEnrichmentKey(teamId, provider)

  const query = useQuery<TeamEnrichmentStatus>({
    queryKey: key,
    enabled: !!teamId,
    queryFn: () => getTeamEnrichmentStatus(teamId!, provider),
  })

  const saveMutation = useMutation({
    mutationKey: ['teamEnrichment', 'save', teamId, provider],
    mutationFn: (apiKey: string) => {
      if (!teamId) throw new Error('No team')
      return setTeamEnrichmentApiKey({ teamId, provider, apiKey })
    },
    onSuccess: (status) => {
      qc.setQueryData(key, status)
      window.dispatchEvent(new CustomEvent('fwdslash:team-enrichment-changed'))
    },
  })

  const disableMutation = useMutation({
    mutationKey: ['teamEnrichment', 'disable', teamId, provider],
    mutationFn: () => {
      if (!teamId) throw new Error('No team')
      return disableTeamEnrichment(teamId, provider)
    },
    onSuccess: (status) => {
      qc.setQueryData(key, status)
      window.dispatchEvent(new CustomEvent('fwdslash:team-enrichment-changed'))
    },
  })

  return {
    status: query.data ?? null,
    loading: !!teamId && query.isPending,
    error: query.error instanceof Error ? query.error.message : null,
    refresh: () => qc.invalidateQueries({ queryKey: key }),
    saveApiKey: async (apiKey: string) => {
      try {
        const status = await saveMutation.mutateAsync(apiKey)
        return { ok: true as const, status }
      } catch (e) {
        return { ok: false as const, error: e instanceof Error ? e.message : 'Save failed' }
      }
    },
    disable: async () => {
      try {
        const status = await disableMutation.mutateAsync()
        return { ok: true as const, status }
      } catch (e) {
        return { ok: false as const, error: e instanceof Error ? e.message : 'Disable failed' }
      }
    },
    saving: saveMutation.isPending,
    disabling: disableMutation.isPending,
  }
}
